import { useCallback } from 'react';
import { create } from 'zustand';

export interface PipListContextItem {
    id: string;
}

export interface PipNavigationContext {
    type:
        | 'event-feed'
        | 'events-page'
        | 'query-results'
        | 'similar-results'
        | 'sources-page'
        | 'report-events';
    items: PipListContextItem[];
    currentIndex: number;
    canLoadMore?: boolean;
    loadMore?: () => Promise<void> | void;
}

export type PipContentMode = 'event' | 'source' | 'report-event';

export const DEFAULT_PIP_ASPECT_RATIO = 16 / 9;

interface PipPlayerState {
    isOpen: boolean;
    mode: PipContentMode;
    eventId: string | null;
    sourceId: string | null;
    navigationContext: PipNavigationContext | null;
    pendingNextIndex: number | null;
    isPlaying: boolean;
    pauseToken: number;
    videoAspectRatio: number;
    setVideoAspectRatio: (aspectRatio: number) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    requestPause: () => void;
    open: (payload: {
        eventId: string;
        navigationContext?: PipNavigationContext | null;
    }) => void;
    openSource: (payload: {
        sourceId: string;
        navigationContext?: PipNavigationContext | null;
    }) => void;
    openReportEvent: (payload: {
        reportEventId: string;
        navigationContext?: PipNavigationContext | null;
    }) => void;
    close: () => void;
    replaceEvent: (eventId: string) => void;
    refreshNavigationContext: (payload: {
        type: PipNavigationContext['type'];
        items: PipListContextItem[];
        canLoadMore?: boolean;
        loadMore?: () => Promise<void> | void;
    }) => void;
    goNext: () => Promise<void>;
    goPrev: () => void;
}

export const usePipPlayerStore = create<PipPlayerState>((set, get) => ({
    isOpen: false,
    mode: 'event',
    eventId: null,
    sourceId: null,
    navigationContext: null,
    pendingNextIndex: null,
    isPlaying: false,
    pauseToken: 0,
    videoAspectRatio: DEFAULT_PIP_ASPECT_RATIO,

    setVideoAspectRatio: (aspectRatio) => {
        if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) return;
        set({ videoAspectRatio: aspectRatio });
    },

    setIsPlaying: (isPlaying) => set({ isPlaying }),

    requestPause: () =>
        set((state) => ({
            isPlaying: false,
            pauseToken: state.pauseToken + 1,
        })),

    open: ({ eventId, navigationContext }) => {
        set({
            isOpen: true,
            mode: 'event',
            eventId,
            sourceId: null,
            navigationContext: navigationContext ?? null,
            pendingNextIndex: null,
            isPlaying: true,
        });
    },

    openSource: ({ sourceId, navigationContext }) => {
        set({
            isOpen: true,
            mode: 'source',
            eventId: null,
            sourceId,
            navigationContext: navigationContext ?? null,
            pendingNextIndex: null,
            isPlaying: true,
        });
    },

    openReportEvent: ({ reportEventId, navigationContext }) => {
        set({
            isOpen: true,
            mode: 'report-event',
            eventId: reportEventId,
            sourceId: null,
            navigationContext: navigationContext ?? null,
            pendingNextIndex: null,
            isPlaying: true,
        });
    },

    close: () => {
        set({
            isOpen: false,
            eventId: null,
            sourceId: null,
            navigationContext: null,
            pendingNextIndex: null,
            isPlaying: false,
        });
    },

    replaceEvent: (eventId) => {
        set({
            eventId,
            mode: 'event',
            sourceId: null,
            pendingNextIndex: null,
        });
    },

    refreshNavigationContext: ({ type, items, canLoadMore, loadMore }) => {
        const { navigationContext, mode, sourceId, eventId, pendingNextIndex } =
            get();
        if (!navigationContext || navigationContext.type !== type) return;

        const activeId = mode === 'source' ? sourceId : eventId;
        const currentIndex = activeId
            ? items.findIndex((item) => item.id === activeId)
            : -1;
        const nextIndex =
            pendingNextIndex !== null && pendingNextIndex < items.length
                ? pendingNextIndex
                : null;
        const resolvedIndex =
            nextIndex ??
            (currentIndex >= 0
                ? currentIndex
                : Math.min(navigationContext.currentIndex, items.length - 1));
        const nextId = nextIndex !== null ? items[nextIndex]?.id : null;
        const idField = mode === 'source' ? 'sourceId' : 'eventId';

        set({
            ...(nextId
                ? {
                      [idField]: nextId,
                      [mode === 'source' ? 'eventId' : 'sourceId']: null,
                  }
                : {}),
            pendingNextIndex:
                nextIndex !== null || !canLoadMore ? null : pendingNextIndex,
            navigationContext: {
                ...navigationContext,
                items,
                currentIndex: resolvedIndex,
                canLoadMore,
                loadMore,
            },
        });
    },

    goNext: async () => {
        const { navigationContext, mode } = get();
        if (!navigationContext) return;

        const { items, currentIndex, canLoadMore, loadMore } =
            navigationContext;
        const nextIndex = currentIndex + 1;

        if (nextIndex < items.length) {
            const idField = mode === 'source' ? 'sourceId' : 'eventId';
            set({
                [idField]: items[nextIndex].id,
                [mode === 'source' ? 'eventId' : 'sourceId']: null,
                navigationContext: {
                    ...navigationContext,
                    currentIndex: nextIndex,
                },
                pendingNextIndex: null,
            });
            return;
        }

        if (canLoadMore && loadMore) {
            set({ pendingNextIndex: nextIndex });
            await loadMore();

            const updated = get().navigationContext;
            if (!updated || nextIndex >= updated.items.length) return;
            const idField = mode === 'source' ? 'sourceId' : 'eventId';
            set({
                [idField]: updated.items[nextIndex].id,
                [mode === 'source' ? 'eventId' : 'sourceId']: null,
                navigationContext: {
                    ...updated,
                    currentIndex: nextIndex,
                },
                pendingNextIndex: null,
            });
        }
    },

    goPrev: () => {
        const { navigationContext, mode } = get();
        if (!navigationContext) return;

        const prevIndex = navigationContext.currentIndex - 1;
        if (prevIndex < 0) return;

        const idField = mode === 'source' ? 'sourceId' : 'eventId';
        set({
            [idField]: navigationContext.items[prevIndex].id,
            [mode === 'source' ? 'eventId' : 'sourceId']: null,
            navigationContext: {
                ...navigationContext,
                currentIndex: prevIndex,
            },
            pendingNextIndex: null,
        });
    },
}));

export function isPipPlayingEvent(
    state: Pick<PipPlayerState, 'isOpen' | 'mode' | 'eventId' | 'isPlaying'>,
    eventId: string,
) {
    return (
        state.isOpen &&
        state.isPlaying &&
        state.mode === 'event' &&
        state.eventId === eventId
    );
}

export function isPipPlayingSource(
    state: Pick<PipPlayerState, 'isOpen' | 'mode' | 'sourceId' | 'isPlaying'>,
    sourceId: string,
) {
    return (
        state.isOpen &&
        state.isPlaying &&
        state.mode === 'source' &&
        state.sourceId === sourceId
    );
}

export function isPipPlayingReportEvent(
    state: Pick<PipPlayerState, 'isOpen' | 'mode' | 'eventId' | 'isPlaying'>,
    reportEventId: string,
) {
    return (
        state.isOpen &&
        state.isPlaying &&
        state.mode === 'report-event' &&
        state.eventId === reportEventId
    );
}

export const usePipWatchingEvent = (eventId: string) =>
    usePipPlayerStore((state) => isPipPlayingEvent(state, eventId));

export const usePipWatchingSource = (sourceId: string) =>
    usePipPlayerStore((state) => isPipPlayingSource(state, sourceId));

export function usePipEventWatcher() {
    const isOpen = usePipPlayerStore((state) => state.isOpen);
    const mode = usePipPlayerStore((state) => state.mode);
    const eventId = usePipPlayerStore((state) => state.eventId);
    const isPlaying = usePipPlayerStore((state) => state.isPlaying);
    return useCallback(
        (id: string) =>
            isPipPlayingEvent({ isOpen, mode, eventId, isPlaying }, id),
        [isOpen, mode, eventId, isPlaying],
    );
}

export function usePipReportEventWatcher() {
    const isOpen = usePipPlayerStore((state) => state.isOpen);
    const mode = usePipPlayerStore((state) => state.mode);
    const eventId = usePipPlayerStore((state) => state.eventId);
    const isPlaying = usePipPlayerStore((state) => state.isPlaying);
    return useCallback(
        (id: string) =>
            isPipPlayingReportEvent({ isOpen, mode, eventId, isPlaying }, id),
        [isOpen, mode, eventId, isPlaying],
    );
}

export const selectCanGoNext = (state: PipPlayerState) => {
    if (!state.navigationContext) return false;
    const { items, currentIndex, canLoadMore } = state.navigationContext;
    return currentIndex < items.length - 1 || !!canLoadMore;
};

export const selectCanGoPrev = (state: PipPlayerState) => {
    if (!state.navigationContext) return false;
    return state.navigationContext.currentIndex > 0;
};
